// src/utils/apiUtils.js
import userManager, { redirectToSignIn } from "../auth/oidcConfig";

/**
 * Wait for user to be available with retries
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} delay - Delay between retries in ms
 * @returns {Promise<object>} - User object with valid token
 */
const waitForUser = async (maxRetries = 5, delay = 1000) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const user = await userManager.getUser();
      if (user && !user.expired && user.access_token) {
        return user;
      }

      // If no user or expired, wait and try again
      if (i < maxRetries - 1) {
        console.log(
          `Waiting for authentication... attempt ${i + 1}/${maxRetries}`
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    } catch (error) {
      console.error(`Error getting user on attempt ${i + 1}:`, error);
      if (i < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw new Error("User authentication not available after retries");
};

/**
 * Enhanced fetch function that automatically handles token renewal and timing issues
 * @param {string} url - The API endpoint URL
 * @param {object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<Response>} - The fetch response
 */
export const authenticatedFetch = async (url, options = {}) => {
  try {
    // Wait for the user to be available (handles login timing issues)
    let user = await waitForUser();

    // Renewal itself is handled by oidc-client's own automaticSilentRenew
    // background timer (see oidcConfig.js) — doing it again here too meant
    // every concurrent authenticatedFetch call near the expiry window could
    // independently kick off its own silent-renewal iframe, racing the
    // background timer's and each other's. That produced real, reproducible
    // OperationCanceledExceptions server-side when one iframe's in-flight
    // request got superseded/torn down mid-DB-query. If the token is
    // already actually expired, the 401/opaqueredirect handling below still
    // catches it and renews before retrying.

    // Prepare headers with authorization
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${user.access_token}`,
      ...options.headers,
    };

    // Make the API call
    const response = await fetch(url, {
      ...options,
      headers,
      // Don't automatically follow redirects
      redirect: 'manual'
    });

    // If we get a 401, the token might have expired between checks
    if (response.status === 401) {
      console.log("Received 401, attempting token refresh...");

      try {
        // Try to refresh the token
        user = await userManager.signinSilent();

        // Retry the request with new token
        const retryHeaders = {
          ...headers,
          Authorization: `Bearer ${user.access_token}`,
        };

        return fetch(url, {
          ...options,
          headers: retryHeaders,
          redirect: 'manual'
        });
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        // Redirect to login if refresh fails
        await redirectToSignIn();
        throw new Error("Authentication required");
      }
    }

    // With `redirect: 'manual'` above, the browser never exposes a followed
    // redirect as status 302 — it comes back as an opaque response
    // (type "opaqueredirect", status 0, no readable headers or body). In
    // this app that only ever happens when the cookie-auth fallback
    // challenges an expired/invalid Bearer token, so treat it the same as
    // a 401: try silent renewal and retry, or send the user to sign in.
    if (response.type === "opaqueredirect") {
      console.log("Received an auth redirect, attempting token refresh...");

      try {
        user = await userManager.signinSilent();

        const retryHeaders = {
          ...headers,
          Authorization: `Bearer ${user.access_token}`,
        };

        return fetch(url, {
          ...options,
          headers: retryHeaders,
          redirect: "manual",
        });
      } catch (refreshError) {
        console.error("Token refresh failed after auth redirect:", refreshError);
        await redirectToSignIn();
        throw new Error("Authentication required - session expired");
      }
    }

    return response;
  } catch (error) {
    console.error("API call error:", error);
    throw error;
  }
};

/**
 * Convenience method for GET requests
 * @param {string} url - The API endpoint URL
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>} - The fetch response
 */
export const authenticatedGet = async (url, options = {}) => {
  try {
    console.log(`GET request to: ${url}`);
    const response = await authenticatedFetch(url, { 
      method: "GET",
      ...options
    });
    
    // Special handling for encryption/session-status endpoint
    if (url.includes('/encryption/status') || url.includes('/encryption/session-status')) {
      const contentType = response.headers.get('content-type');
      
      // If we're getting HTML when expecting JSON, the session is likely expired
      if (contentType && contentType.includes('text/html')) {
        console.log("Encryption status returned HTML - wrapping in proper JSON response");
        
        // Create a new response object with proper authentication status
        const jsonResponse = {
          authenticated: false,
          requiresAuthentication: true,
          securityLevel: "Maximum",
          message: "Session expired, authentication required"
        };
        
        // Create a new Response object with our JSON
        const modifiedResponse = new Response(JSON.stringify(jsonResponse), {
          status: 200,
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        return modifiedResponse;
      }
    }
    
    // Do not throw for non-2xx responses, always return the response
    return response;
  } catch (error) {
    console.error(`GET request error for ${url}:`, error);
    // If error has a response, return it for error handling
    if (error && error.response) return error.response;
    throw error;
  }
};

/**
 * Convenience method for POST requests
 * @param {string} url - The API endpoint URL
 * @param {object} data - The data to send in the request body
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>} - The fetch response
 */
export const authenticatedPost = async (url, data, options = {}) => {
  try {
    console.log(`POST request to: ${url}`);
    const response = await authenticatedFetch(url, {
      method: "POST",
      body: JSON.stringify(data),
      ...options
    });
    
    // Do not throw for non-2xx responses, always return the response
    return response;
  } catch (error) {
    console.error(`POST request error for ${url}:`, error);
    // If error has a response, return it for error handling
    if (error && error.response) return error.response;
    throw error;
  }
};

/**
 * Convenience method for PUT requests
 */
export const authenticatedPut = (url, data) => {
  return authenticatedFetch(url, {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

/**
 * Convenience method for DELETE requests
 */
export const authenticatedDelete = (url) => {
  return authenticatedFetch(url, { method: "DELETE" });
};
